import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { VehicleField } from "@/pages/DriverStatus.tsx/components/DriverStatusInfoPopup";
import { useGetVehiclesQuery } from "@/requests/transport.queries";
import { useUpdateUserVehicleMutation } from "@/requests/user.mutations";
import { useUsersShowVehicleUpdateDialog, useUsersTableActions } from "@/stores/use-users-table";
import { User } from "@/types/user.types";

const formSchema = z.object({
    vehicleId: z.number().optional(),
});

export type UpdateVehicleType = z.infer<typeof formSchema>;
function UpdateVehicleDialog() {
    const { t } = useTranslation();
    const { updateShowVehicleUpdateDialog } = useUsersTableActions();
    const user = useUsersShowVehicleUpdateDialog() as User;
    const { isPending: loading, mutateAsync } = useUpdateUserVehicleMutation();
    const { data: vehicleData } = useGetVehiclesQuery(false);

    const vehicles = useMemo(() => (vehicleData ? vehicleData.map((item) => item.properties) : []), [vehicleData]);

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = useForm({
        defaultValues: {
            vehicleId: user?.vehicleData?.id ? +user.vehicleData?.id : undefined,
        },
        disabled: loading,
        resolver: zodResolver(formSchema),
    });

    const createUser = async (data: UpdateVehicleType) => {
        if (loading || !user.id) return;
        try {
            const res = await mutateAsync({ vehicleId: data.vehicleId || null, id: user.id });
            const message = res.message || "";
            if (res.ok) {
                updateShowVehicleUpdateDialog();
                enqueueSnackbar(message, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("User error:", error);
        }
    };
    const vehicleId = watch("vehicleId");
    const handleClose = () => {
        updateShowVehicleUpdateDialog();
    };
    if (!user) return null;

    return (
        <>
            <DialogContent dividers>
                <form autoComplete="off" onSubmit={handleSubmit(createUser)} className="grid grid-cols-7 gap-7">
                    <VehicleField
                        vehicles={vehicles}
                        loading={loading}
                        errors={errors}
                        control={control}
                        vehicleId={vehicleId}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    disabled={user.vehicleData?.id == vehicleId}
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(createUser)}
                >
                    {t("buttons.update")}
                </LoadingButton>
            </DialogActions>
        </>
    );
}

export default UpdateVehicleDialog;
